import { useMemo } from 'react';

import type { SearchBarFilterOption } from '@fabric-app/business/search/searchbar.types';
import { SearchBarFilterType } from '@fabric-app/business/search/searchbar.types';
import { useQueryResourceRootSpaceSystemList } from '@fabric-app/business/spaces/queries/useQueryResourceRootSpaceSystemList';
import { useQueryTags } from '@fabric-app/business/tags/queries/useQueryTags';
import { renderResourceIcon } from '@fabric-app/business/resource-detail/components/ResourceIcon/renderResourceIcon';
import { filterKindOptions } from '@fabric-app/business/resources/components/Filters/ResourceFilterKind/ResourceFilterKind';

export const usePillAutocomplete = (filterType: SearchBarFilterType, query?: string) => {
  const { data: tags, isLoading: isLoadingTags } = useQueryTags(query, {
    enabled: filterType === SearchBarFilterType.TAG,
  });

  const { spaceRoots, isLoading: isLoadingSpaces } = useQueryResourceRootSpaceSystemList(
    undefined,
    {
      includeInbox: true,
    },
  );

  const options: SearchBarFilterOption[] = useMemo(() => {
    switch (filterType) {
      case SearchBarFilterType.TAG:
        return (
          tags?.map((tag) => ({
            label: tag.name,
            value: tag.id,
            canHaveChildren: false,
          })) ?? []
        );
      case SearchBarFilterType.CONTENT_TYPE: {
        return filterKindOptions
          .map((option) => ({
            label: option.stringLabel,
            value: option.value,
            canHaveChildren: false,
          }))
          .filter(
            (item) =>
              item.value !== 'placeholder' &&
              item.label.toLowerCase().includes(query?.toLowerCase() ?? ''),
          );
      }
      case SearchBarFilterType.LOCATION:
        return (
          spaceRoots?.map((rootSpace) => ({
            label: rootSpace.folder.name || 'Untitled',
            value: rootSpace.id,
            canHaveChildren: true,
            type: 'space' as const,
            // @ts-expect-error for now the folder doesn't contain icon
            icon: renderResourceIcon(rootSpace.folder, { size: 24 }),
          })) ?? []
        ).filter((option) => option.label.toLowerCase().includes(query?.toLowerCase() ?? ''));
      default:
        return [];
    }
  }, [tags, spaceRoots, filterType, query]);

  const isLoading = useMemo(() => {
    switch (filterType) {
      case SearchBarFilterType.TAG:
        return isLoadingTags;
      case SearchBarFilterType.LOCATION:
        return isLoadingSpaces;
      default:
        return false;
    }
  }, [isLoadingTags, filterType, isLoadingSpaces]);

  return {
    options,
    isLoading,
  };
};
